import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { FiSave, FiMail, FiSend, FiCheck, FiX, FiRefreshCw, FiInfo } from 'react-icons/fi';

const API_URL = getApiUrl();

export default function EmailConfigPage() {
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_ssl: true
  });
  const [allSettings, setAllSettings] = useState(null); // Store all settings for merge on save
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Store all settings for merging on save
        setAllSettings(data);
        setConfig(prev => ({
          ...prev,
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 465,
          smtp_user: data.smtp_user || '',
          smtp_password: data.smtp_password || '',
          smtp_from_email: data.smtp_from_email || data.email || '',
          smtp_from_name: data.smtp_from_name || data.shop_name || '',
          smtp_ssl: data.smtp_ssl !== false
        }));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Merge SMTP config with all existing settings to avoid validation errors
      const dataToSave = {
        ...allSettings,
        ...config
      };
      
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        toast.success('Configuration SMTP enregistrée');
      } else {
        const errorData = await response.json();
        console.error('Save error:', errorData);
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Entrez une adresse email de test');
      return;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/email/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ to_email: testEmail })
      });

      const data = await response.json();
      if (response.ok) {
        setTestResult({ success: true, message: 'Email envoyé avec succès !' });
        toast.success('Email de test envoyé');
      } else {
        setTestResult({ success: false, message: data.detail || 'Échec de l\'envoi' });
        toast.error(data.detail || 'Échec de l\'envoi');
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Erreur de connexion' });
      toast.error('Erreur de connexion');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuration Email (SMTP)</h1>
          <p className="text-slate-500 mt-1">Paramètres pour l'envoi des emails</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <FiSave className="mr-2 h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SMTP Config */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FiMail className="text-amber-600" />
              Serveur SMTP
            </CardTitle>
            <CardDescription>Paramètres de connexion au serveur d'envoi</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Serveur SMTP</Label>
              <Input
                value={config.smtp_host}
                onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                placeholder="smtp.gmail.com, smtp.office365.com..."
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Port</Label>
                <Input
                  type="number"
                  value={config.smtp_port}
                  onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) || 465 })}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">465 (SSL) ou 587 (TLS)</p>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={config.smtp_ssl}
                    onCheckedChange={(v) => setConfig({ ...config, smtp_ssl: v })}
                  />
                  <span className="text-sm">SSL (port 465)</span>
                </label>
              </div>
            </div>

            {/* PlanetHoster Info */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-1 flex items-center gap-1">
                <FiInfo className="h-4 w-4" /> PlanetHoster
              </p>
              <p className="text-blue-700">
                Serveur: mail.votre-domaine.com<br />
                Port: 465 (SSL activé)
              </p>
            </div>

            <div>
              <Label>Identifiant (email ou username)</Label>
              <Input
                value={config.smtp_user}
                onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                placeholder="votre@email.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Mot de passe</Label>
              <Input
                type="password"
                value={config.smtp_password}
                onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                placeholder="••••••••"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Pour Gmail, utilisez un mot de passe d'application
              </p>
            </div>
          </CardContent>
        </Card>

        {/* From & Test */}
        <Card>
          <CardHeader>
            <CardTitle>Expéditeur</CardTitle>
            <CardDescription>Informations affichées dans les emails envoyés</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Email d'expédition</Label>
              <Input
                value={config.smtp_from_email}
                onChange={(e) => setConfig({ ...config, smtp_from_email: e.target.value })}
                placeholder="contact@votreentreprise.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Nom d'expéditeur</Label>
              <Input
                value={config.smtp_from_name}
                onChange={(e) => setConfig({ ...config, smtp_from_name: e.target.value })}
                placeholder="Just A Smile Events"
                className="mt-1"
              />
            </div>

            {/* Test Section */}
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-3">Tester la configuration</h4>
              <div className="flex gap-2">
                <Input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="email@test.com"
                  className="flex-1"
                />
                <Button onClick={handleTest} disabled={testing} variant="outline">
                  {testing ? (
                    <FiRefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <FiSend className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {testResult && (
                <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                  testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {testResult.success ? <FiCheck /> : <FiX />}
                  {testResult.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Help */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <h4 className="font-semibold text-blue-900 mb-3">Configuration courantes</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-blue-800">Gmail</p>
              <p className="text-blue-700">smtp.gmail.com:587</p>
              <p className="text-blue-600 text-xs">Mot de passe d'app requis</p>
            </div>
            <div>
              <p className="font-medium text-blue-800">OVH</p>
              <p className="text-blue-700">ssl0.ovh.net:587</p>
            </div>
            <div>
              <p className="font-medium text-blue-800">Office 365</p>
              <p className="text-blue-700">smtp.office365.com:587</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
